import React, { useState } from 'react';
import {
    SelectionPanelGroup,
    SelectionPanelGroupTheme,
    SelectionPanelGroupSelectionType,
    SelectionPanelGroupStyleMode,
    SelectionPanelCheckShape,
    SelectionPanelCheckStyle,
    SelectionPanelGroupLayoutStyle,
} from '@apply/components';
import { DemoExample } from '../../common/DemoLayout';

/** Condensed style: a compact checklist with a square check, unless checkShape says otherwise. */
export const CondensedPanel: React.FC = () => {
    const [condensedSelections, set_condensedSelections] = useState(['delinquentSupplier']);
    const [condensedCircleMultiSelections, set_condensedCircleMultiSelections] = useState(['delinquentSupplier']);
    const [condensedCircleSelections, set_condensedCircleSelections] = useState(['delinquentSupplier']);
    const [condensedSquareSingleSelections, set_condensedSquareSingleSelections] = useState(['delinquentSupplier']);
    const [fillCircleSelections, set_fillCircleSelections] = useState(['always']);
    const [fillSquareSelections, set_fillSquareSelections] = useState(['always']);
    const [labelVerticalSelections, set_labelVerticalSelections] = useState(['always']);
    const [labelHorizontalSelections, set_labelHorizontalSelections] = useState(['always']);
    const [labelHorizontalWrappedSelections, set_labelHorizontalWrappedSelections] = useState(['always']);
    const [circleFillHorizontalSelections, set_circleFillHorizontalSelections] = useState(['always']);
    const [circleFillHorizontalWrappedSelections, set_circleFillHorizontalWrappedSelections] = useState(['always']);

    const frequencyOptions = [
        { id: 'always', children: 'Always' },
        { id: 'sometimes', children: 'Sometimes' },
        { id: 'never', children: 'Never' },
    ];

    const condensedOptions = [
        { id: 'felony', children: 'Criminal or felony conviction' },
        { id: 'litigation', children: 'Litigation with financial institution in last 12 months' },
        { id: 'liens', children: 'Outstanding real estate liens' },
        { id: 'delinquentLoan', children: '30+ days delinquent on real estate loan' },
        { id: 'delinquentSupplier', children: '30+ days delinquent to primary building supplier' },
        { id: 'foreclosure', children: 'Foreclosure within past 10 years' },
        { id: 'bankruptcy', children: 'Bankruptcy within past 10 years' },
        { id: 'bfiPayment', children: 'BFI payment issue in past 12 months' },
    ];

    return (
        <>
            <DemoExample label='Blue Theme (Multiselect - Condensed)'>
                <SelectionPanelGroup
                    id='blueCondensedRPDemo'
                    items={condensedOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={condensedSelections}
                    set_selectedPanels={set_condensedSelections}
                    selectionType={SelectionPanelGroupSelectionType.Multiple}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Multiselect - Condensed, Circle Check)'>
                <SelectionPanelGroup
                    id='blueCondensedCircleMultiRPDemo'
                    items={condensedOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={condensedCircleMultiSelections}
                    set_selectedPanels={set_condensedCircleMultiSelections}
                    selectionType={SelectionPanelGroupSelectionType.Multiple}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Circle}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, Circle Check)'>
                <SelectionPanelGroup
                    id='blueCondensedCircleRPDemo'
                    items={condensedOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={condensedCircleSelections}
                    set_selectedPanels={set_condensedCircleSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Circle}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, Square Check)'>
                <SelectionPanelGroup
                    id='blueCondensedSquareRPDemo'
                    items={condensedOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={condensedSquareSingleSelections}
                    set_selectedPanels={set_condensedSquareSingleSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Square}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, Circle Fill)'>
                <SelectionPanelGroup
                    id='blueCondensedCircleFillRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={fillCircleSelections}
                    set_selectedPanels={set_fillCircleSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Circle}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, Square Fill)'>
                <SelectionPanelGroup
                    id='blueCondensedSquareFillRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={fillSquareSelections}
                    set_selectedPanels={set_fillSquareSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Square}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, With Label)'>
                <SelectionPanelGroup
                    id='blueCondensedLabelRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={labelVerticalSelections}
                    set_selectedPanels={set_labelVerticalSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                    label='Self Owned?'
                    showInfoIcon={true}
                    infoText='Is the property owned by the borrower?'
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, With Label, Horizontal)'>
                <SelectionPanelGroup
                    id='blueCondensedLabelHorizontalRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={labelHorizontalSelections}
                    set_selectedPanels={set_labelHorizontalSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                    label='Self Owned?'
                    layoutStyle={SelectionPanelGroupLayoutStyle.Horizontal}
                    labelWidth='180px'
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, With Label, Horizontal, Wrapped)'>
                <SelectionPanelGroup
                    id='blueCondensedLabelHorizontalWrappedRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={labelHorizontalWrappedSelections}
                    set_selectedPanels={set_labelHorizontalWrappedSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                    label='Self Owned?'
                    layoutStyle={SelectionPanelGroupLayoutStyle.Horizontal}
                    labelWidth='180px'
                    wrapPanels={true}
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, Circle Fill, With Label, Horizontal)'>
                <SelectionPanelGroup
                    id='blueCondensedCircleFillHorizontalRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={circleFillHorizontalSelections}
                    set_selectedPanels={set_circleFillHorizontalSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Circle}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                    label='Self Owned?'
                    layoutStyle={SelectionPanelGroupLayoutStyle.Horizontal}
                    labelWidth='180px'
                />
            </DemoExample>
            <DemoExample label='Blue Theme (Single Select - Condensed, Circle Fill, With Label, Horizontal, Wrapped)'>
                <SelectionPanelGroup
                    id='blueCondensedCircleFillHorizontalWrappedRPDemo'
                    items={frequencyOptions}
                    theme={SelectionPanelGroupTheme.Blue}
                    selectedPanels={circleFillHorizontalWrappedSelections}
                    set_selectedPanels={set_circleFillHorizontalWrappedSelections}
                    styleMode={SelectionPanelGroupStyleMode.Condensed}
                    checkShape={SelectionPanelCheckShape.Circle}
                    checkStyle={SelectionPanelCheckStyle.Fill}
                    label='Self Owned?'
                    layoutStyle={SelectionPanelGroupLayoutStyle.Horizontal}
                    labelWidth='180px'
                    wrapPanels={true}
                    showInfoIcon={true}
                />
            </DemoExample>
        </>
    );
};
